/**
 * Shared buyer purchase gate for item detail and listing preview.
 * One owner for auth → profile-complete → ready (open payment UI).
 */
export type BuyerPurchaseGateUser = {
  phone?: string | null;
  gender?: string | null;
  name?: string | null;
  location?: string | null;
  email?: string | null;
  is_profile_complete?: boolean | null;
};

export type BuyerPurchaseGateResult =
  | { kind: "ready" }
  | { kind: "require_auth"; loginHref: string }
  | { kind: "complete_profile"; completeProfileHref: string };

/** Query flag: land on `/item/[id]` and open the purchase modal when eligible. */
export const ITEM_BUY_INTENT_PARAM = "buy";
export const ITEM_BUY_INTENT_VALUE = "1";

export function buildItemBuyHref(itemId: string): string {
  return `/item/${itemId}?${ITEM_BUY_INTENT_PARAM}=${ITEM_BUY_INTENT_VALUE}`;
}

export function itemPageHasBuyIntent(buyParam: string | null): boolean {
  return buyParam === ITEM_BUY_INTENT_VALUE || buyParam === "true";
}

export function isBuyerProfileCompleteForPurchase(
  user: BuyerPurchaseGateUser | null | undefined,
): boolean {
  if (!user) return false;
  const nameParts = (user.name ?? "").trim().split(/\s+/).filter(Boolean);
  return Boolean(
    user.phone &&
      user.gender &&
      user.name &&
      user.location &&
      nameParts.length >= 2 &&
      user.is_profile_complete,
  );
}

/**
 * Decide the next step for “Buy this item” (detail page or preview CTA).
 * Callers own navigation / opening the payment modal.
 */
export function evaluateBuyerPurchaseGate(params: {
  itemId: string;
  isAuthenticated: boolean;
  user: BuyerPurchaseGateUser | null | undefined;
  /** Path to return to after auth/profile (defaults to buy-intent item URL). */
  returnPath?: string;
}): BuyerPurchaseGateResult {
  const returnPath = params.returnPath ?? buildItemBuyHref(params.itemId);

  if (!params.isAuthenticated) {
    return {
      kind: "require_auth",
      loginHref: `/login?redirectTo=${encodeURIComponent(returnPath)}`,
    };
  }

  if (!isBuyerProfileCompleteForPurchase(params.user)) {
    const email = params.user?.email ?? "";
    return {
      kind: "complete_profile",
      completeProfileHref: `/complete-profile?email=${encodeURIComponent(email)}&redirectTo=${encodeURIComponent(returnPath)}`,
    };
  }

  return { kind: "ready" };
}
